import styled, { keyframes } from 'styled-components'
import { PillButton } from '../components/common/stand_alone/pillButton'
import { breakpoints } from '../assets/styles/mediaBreakpoints'
import { EXTERNAL_LINK } from '../assets/data/external_link'

const AboutPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroTextContainer>
          <HeroText $shadow={true}>
            We believe in the transformative power of collective action to build
            a better world
          </HeroText>
        </HeroTextContainer>
        <ArrowContainer>
          <Arrow src="/icons/icon-arrow-down.svg"></Arrow>
        </ArrowContainer>
      </HeroSection>
      <AboutSection>
        <LogoGreen src="/images/about/logo-green.png" />
        <AboutText>
          We are a Social Learning Platform that amplifies the Voice of Science,
          empowering passionate individuals to take meaningful action, advance
          the SDGs, and create positive change for our planet
        </AboutText>
        <PillButton
          title="What's SDG goals"
          textColor="black"
          bg="white"
          link={EXTERNAL_LINK.un}
        />
      </AboutSection>
      <BeginningSection>
        <IframeVideo
          src={EXTERNAL_LINK.youtubeAbout}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></IframeVideo>
        <TextContainerWrapper>
          <TextContainer>
            <SubHeading>THE BEGINNING</SubHeading>
            <SectionHeading>The Tale of Oscar the Oak</SectionHeading>
            <BodyText>
              We believe in the power of education advocacy and collective
              action together. We can accelerate change, bridging the gap
              between today’s culture and the timeless wisdom of science.
            </BodyText>
          </TextContainer>
        </TextContainerWrapper>
      </BeginningSection>
      <MissionSection>
        <MissionContentImageContainer>
          <MissionContent>
            <TextContainer>
              <SubHeading>OUR MISSION</SubHeading>
              <SectionHeading>Humanity lives</SectionHeading>
              <BodyText>
                Our mission is to rebuild trust in science and empower
                individuals to take meaningful actions that promote
                sustainability—from reducing carbon footprints to preserving
                biodiversity. Every step brings us closer to a future where
                humanity thrives in harmony with nature.
              </BodyText>
            </TextContainer>
          </MissionContent>
          <MissionImage src="/images/about/Mission-Section-All-device.jpg" />
        </MissionContentImageContainer>
      </MissionSection>
      <ValueSection>
        <SubHeading>OUR VALUE</SubHeading>
        <ValueCardsContainer>
          <ValueCard
            title="Transparency"
            description="Promoting open access to plain-language scientific data and combating misinformation by fostering honest communication."
          />
          <ValueCard
            title="Empowerment"
            description="Equipping individuals with the tools and knowledge to drive positive change."
          />
          <ValueCard
            title="Sustainability"
            description="Equipping individuals with the tools and knowledge to drive positive change."
          />
          <ValueCard
            title="Empowerment"
            description="Creating a vibrant network of engaged, informed, and empowered citizens."
          />
        </ValueCardsContainer>
      </ValueSection>
      <VisionSection>
        <VisionText>
          We believe in the power of advocacy through education and collective
          action. Together, we can accelerate change by bridging the gap between
          modern culture and the enduring wisdom of science.
        </VisionText>
      </VisionSection>
      <SuperHeroSection>
        <TextContainer $width="40%">
          <SectionHeading>Meet Our Superheroes</SectionHeading>
          <BodyText>
            Every great story needs a hero, and at the Community of Guardians,
            that hero is YOU. Uncover your unique superpower and your role in
            our global mission alongside our SDG Superheroes, inspired by the 17
            Sustainable Development Goals (SDGs).
          </BodyText>
          <PillButton link="/superheroes" title="All Superheroes" />
        </TextContainer>
        <SlideWrapper>
          <HeroesImages />
        </SlideWrapper>
      </SuperHeroSection>
      <ContactSection>
        <LogoSymbol src="/images/logos/Logo-Symbol.svg" />
        <SubHeading>Let’s Build a Better Future Together</SubHeading>
        <SectionHeading $textAlign="center">
          Become a Guardian of Changes
        </SectionHeading>
        <ContactTextWrapper>
          <ContactText>
            Whether you’re an individual, nonprofit, or corporate entity,
            partnering with the Community of Guardians means making impactful
            strides toward a sustainable and equitable future.
          </ContactText>
        </ContactTextWrapper>

        <PillButton title="Contact Us" link="/contact" />
      </ContactSection>
      <DonationSection>
        <TextContainer $width="640px" $gap="24px">
          <HeroText>Be Part of the Change</HeroText>
          <BodyText $color="white">
            Every donation, big or small, moves us closer to realizing our
            vision of a sustainable and equitable world. Together, we can
            transform actions into a better world!
          </BodyText>
          <PillButton
            title="Donate Now"
            link={EXTERNAL_LINK.donate}
            bg="white"
            textColor="black"
          />
        </TextContainer>
      </DonationSection>
    </PageContainer>
  )
}

const ValueCard = ({
  title,
  description,
}: {
  title: string
  description: string
}) => {
  return (
    <ValueCardContainer>
      <h5>{title}</h5>
      <p>{description}</p>
    </ValueCardContainer>
  )
}

const HeroesImages = () => {
  return (
    <SuperHeroesSlide>
      {Array.from({ length: 17 }, (_, i) => (
        <SuperHeroImage
          key={i + 1}
          src={`/images/heroes/SDG_${i + 1}.png`}
          alt={`hero${i + 1}`}
        />
      ))}
      {Array.from({ length: 17 }, (_, i) => (
        <SuperHeroImage
          key={i + 1}
          src={`/images/heroes/SDG_${i + 1}.png`}
          alt={`hero${i + 1}`}
        />
      ))}
    </SuperHeroesSlide>
  )
}

const PageContainer = styled.div`
  flex-grow: 1;
`
const HeroSection = styled.div`
  background-image: url('/images/about/Hero-Section-Desktop.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 726px;
  width: 100%;
  padding: 60px 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 20px;
  @media (max-width: ${breakpoints.mobile}px) {
    background-image: url('/images/about/Hero-Section-Mobile.jpg');
    padding: 64px 24px;
    height: 780px;
    justify-content: space-between;
  }
`
const HeroTextContainer = styled.div`
  @media (min-width: ${breakpoints.mobile + 1}px) {
    width: 40%;
  }
`
const HeroText = styled.h1.attrs<{ $shadow?: boolean }>((props) => ({
  $shadow: props.$shadow ?? false,
}))`
  color: ${(props) => props.theme.colors['primary-500']};
  font-size: 56px;
  text-shadow: ${(props) => (props.$shadow ? '4px 4px 16px black' : 'none')};
  @media (max-width: ${breakpoints.desktop}px) {
    font-size: 2rem;
  }
`
const ArrowContainer = styled.div`
  border: 2px solid white;
  border-radius: 50%;
  padding: 5px;
  width: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  @media (max-width: ${breakpoints.mobile}px) {
    width: 70px;
    height: 70px;
    margin-right: auto;
    margin-left: 0;
  }
`
const Arrow = styled.img`
  width: 80px;
  height: 80px;
  filter: invert(89%) sepia(100%) saturate(0%) hue-rotate(72deg)
    brightness(109%) contrast(101%);
  @media (max-width: ${breakpoints.mobile}px) {
    width: 60px;
    height: 60px;
  }
`
const AboutSection = styled.div`
  height: 822px;
  background-color: ${(props) => props.theme.colors['gray-500']};
  width: 100%;
  padding: 141px 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  align-items: flex-start;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 101px 24px;
  }
`
const LogoGreen = styled.img`
  height: 120px;
  @media (max-width: ${breakpoints.desktop}px) {
    height: 100px;
  }
`
const AboutText = styled.h1`
  font-size: 56px;
  background: linear-gradient(
    132deg,
    ${(props) => props.theme.colors['primary-200']},
    ${(props) => props.theme.colors['primary-500']}
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  @media (max-width: ${breakpoints.desktop}px) {
    font-size: 32px;
  }
`
const BeginningSection = styled.div`
  padding: 120px 100px;
  display: flex;
  align-items: center;
  gap: 64px;
  width: 100%;
  height: 667px;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 80px 24px;
    flex-direction: column;
  }
}
`
const IframeVideo = styled.iframe`
  width: 100%;
  height: 100%;
  border-radius: 24px;
  border: none;
`
const TextContainerWrapper = styled.div`
  width: 50%;
  @media (max-width: ${breakpoints.desktop}px) {
    width: 100%;
  }
`
const TextContainer = styled.div.attrs<{ $width?: string; $gap?: string }>(
  (props) => ({
    $width: props.$width || 'auto',
    $gap: props.$gap || '16px',
  }),
)`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap};
  width: ${(props) => props.$width};
  @media (max-width: ${breakpoints.desktop}px) {
    width: 100%;
  }
`
const SubHeading = styled.h3`
  font-size: 16px;
  font-weight: bold;
`
const SectionHeading = styled.h2.attrs<{ $textAlign?: string }>((props) => ({
  $textAlign: props.$textAlign || 'left',
}))`
  font-size: 48px;
  font-weight: bold;
  text-align: ${(props) => props.$textAlign};
  @media (max-width: ${breakpoints.desktop}px) {
    font-size: 40px;
  }
`
const BodyText = styled.p.attrs<{ $color?: string }>((props) => ({
  $color: props.$color || 'black',
}))`
  color: ${(props) => props.$color};
  font-size: 16px;
`
const MissionSection = styled.div`
  padding: 40px 64px;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 40px 24px;
  }
`
const MissionContentImageContainer = styled.div`
  background-color: ${(props) => props.theme.colors['primary-500']};
  padding: 0;
  display: flex;
  border-radius: 24px;
  @media (max-width: ${breakpoints.desktop}px) {
    flex-direction: column;
  }
`
const MissionContent = styled.div`
  padding: 146px 56px;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 40px 24px;
  }
`
const MissionImage = styled.img`
  height: 500px;
  object-fit: cover;
  border-top-right-radius: 24px;
  border-bottom-right-radius: 24px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  @media (max-width: ${breakpoints.desktop}px) {
    height: 320px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 24px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 24px;
  }
`
const ValueSection = styled.div`
  padding: 40px 64px 80px;
  display: flex;
  flex-direction: column;
  // align-items: center;
  gap: 16px;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 40px 24px;
  }
`
const ValueCardsContainer = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 16px;
  overflow-x: auto;
`
const ValueCardContainer = styled.div`
  padding: 32px;
  border-radius: 16px;
  background-color: ${(props) => props.theme.colors['primary-200']};
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 264px;
  max-width: 304px;
`
const VisionSection = styled.div`
  height: 560px;
  width: 100%;
  background-image: url('/images/about/Vision-Section-All-device.jpg');
  object-fit: contain;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  padding: 180px 353px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  @media (max-width: ${breakpoints.desktop}px) {
    height: 800px;
    padding: 180px 24px;
  }
`
const VisionText = styled.h4`
  font-size: 32px;
  color: white;
  font-weight: bold;
  text-shadow: 4px 4px 16px black;
`
const SuperHeroSection = styled.div`
  height: 640px;
  width: 100%;
  padding: 80px 64px;
  display: flex;
  justify-content: center;
  item-align: center;
  gap: 60px;
  @media (max-width: ${breakpoints.desktop}px) {
    height: auto;
    padding: 80px 24px;
    flex-direction: column;
  }
`
const slide = keyframes`
from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
// `
const SuperHeroImage = styled.img`
  height: 480px;
  @media (max-width: ${breakpoints.desktop}px) {
    height: 240px;
  }
`
const SuperHeroesSlide = styled.div`
  display: flex;
  animation: ${slide} 40s linear infinite;
`

const SlideWrapper = styled.div`
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  width: 60%;
  position: relative;
  /* Left Blur */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 50px;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
    z-index: 1;
    pointer-events: none;
  }

  /* Right Blur */
  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 50px;
    background: linear-gradient(
      to left,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
    z-index: 1;
    pointer-events: none;
  }

  :hover {
    animation-play-state: paused;
  }
  @media (max-width: ${breakpoints.desktop}px) {
    width: 100%;
  }
`
const ContactSection = styled.div`
  padding: 140px 64px;
  background-image: url('/images/about/Contact-Section-All-Device.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-direction: column;
  @media (max-width: ${breakpoints.desktop}px) {
    padding: 80px 24px;
  }
`
const LogoSymbol = styled.img`
  height: 192px;
  @media (max-width: ${breakpoints.desktop}px) {
    height: 96px;
  }
`
const ContactTextWrapper = styled.div`
  width: 640px;
  @media (max-width: ${breakpoints.desktop}px) {
    width: 100%;
  }
`
const ContactText = styled.p`
  font-size: 20px;
  text-align: center;
`
const DonationSection = styled.div`
  padding: 140px 64px;
  background-image: url('/images/about/Donation-Section-Desktop.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  width: 100%;
  @media (max-width: ${breakpoints.desktop}px) {
    background-image: url('/images/about/Donation-Section-Mobile.png');
    padding: 80px 24px 280px;
  }
`

export default AboutPage
